import { TransportationOption, Trip } from '@/types';
import { updateTrip } from './tripService';

export const transportationOptionsService = {
  /**
   * Add a new transportation option to a trip
   * @param trip The trip to add the transportation option to
   * @param newTransportationOption The transportation option data (without ID)
   * @returns Promise that resolves when the option is added
   */
  addTransportationOption: async (
    trip: Trip, 
    newTransportationOption: Omit<TransportationOption, 'id'>
  ): Promise<void> => {
    const id = `transportation-option-${Date.now()}`;
    const updatedTrip = {
      ...trip,
      transportationOptions: [
        ...trip.transportationOptions, 
        { ...newTransportationOption, id }
      ]
    };
    await updateTrip(updatedTrip);
  },

  /**
   * Update an existing transportation option in a trip
   * @param trip The trip containing the transportation option
   * @param updatedTransportationOption The updated transportation option data
   * @returns Promise that resolves when the option is updated
   */
  updateTransportationOption: async (
    trip: Trip, 
    updatedTransportationOption: TransportationOption
  ): Promise<void> => {
    const updatedTrip = {
      ...trip,
      transportationOptions: trip.transportationOptions.map(option =>
        option.id === updatedTransportationOption.id ? updatedTransportationOption : option
      )
    };
    await updateTrip(updatedTrip);
  },

  /**
   * Delete a transportation option from a trip
   * @param trip The trip containing the transportation option
   * @param transportationOptionId The ID of the transportation option to delete
   * @returns Promise that resolves when the option is deleted
   */
  deleteTransportationOption: async (
    trip: Trip, 
    transportationOptionId: string
  ): Promise<void> => {
    const updatedTrip = {
      ...trip,
      transportationOptions: trip.transportationOptions.filter(
        option => option.id !== transportationOptionId
      )
    };
    await updateTrip(updatedTrip);
  },

  /**
   * Get all transportation options for a trip
   * @param trip The trip to get transportation options from
   * @returns Array of transportation options
   */
  getTransportationOptions: (trip: Trip): TransportationOption[] => {
    return trip.transportationOptions || [];
  }
};